/**
 * ExportVersionSheet — bottom sheet (dialog on desktop) that copies a saved
 * quote version's details to the clipboard one section at a time, or all at
 * once, or downloads them as a spreadsheet. For estimators who paste quote
 * details into their own proposal / invoicing software.
 *
 * The packet is prefetched by the caller (useVersionExportPacket) so each copy
 * writes to the clipboard synchronously inside the tap — Safari refuses
 * clipboard writes that happen after an await.
 */

import { useState } from 'react';
import { Check, Copy, FileSpreadsheet, Layers, ListChecks, Package, Receipt, Users, X } from 'lucide-react';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { copySections, downloadXlsx } from '../../lib/quoteExport';
import {
  EXPORT_COPY_LABELS,
  exportSectionsForKey as sectionsForKey,
  exportUnavailableReason,
  type ExportCopyKey,
  type QuoteVersionExportPacket,
} from '@iomi/shared';

export { EXPORT_COPY_LABELS };
export type { ExportCopyKey };

interface ExportVersionSheetProps {
  packet: QuoteVersionExportPacket | null;
  loading: boolean;
  error: string | null;
  /** Shown under the title, e.g. "QT-00001-2 · Acme Corp". */
  subtitle: string;
  onClose: () => void;
  /** Called after a successful copy so the page can show its toast. */
  onCopied?: (key: ExportCopyKey) => void;
}

interface OptionDef {
  key: ExportCopyKey;
  label: string;
  hint: string;
  icon: typeof Receipt;
}

const OPTIONS: OptionDef[] = [
  { key: 'lineItems', label: 'Copy line items', hint: 'Charges as quoted: description, rate, hours, total', icon: Receipt },
  { key: 'crew', label: 'Copy crew & trucks', hint: 'Roles, counts, rates, billable hours and method', icon: Users },
  { key: 'materials', label: 'Copy materials', hint: 'Items, quantities and locked prices', icon: Package },
  { key: 'scope', label: 'Copy scope of services', hint: 'Code, service and responsible party', icon: ListChecks },
  { key: 'all', label: 'Copy everything', hint: 'Every section above, plus quote header and totals', icon: Layers },
];

export function ExportVersionSheet({ packet, loading, error, subtitle, onClose, onCopied }: ExportVersionSheetProps) {
  const [copied, setCopied] = useState<ExportCopyKey | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  function unavailableReason(key: ExportCopyKey): string | null {
    return packet ? exportUnavailableReason(packet, key) : null;
  }

  function handleCopy(key: ExportCopyKey) {
    if (!packet) return;
    const sections = sectionsForKey(packet, key);
    if (!sections) return;
    setCopyError(null);
    // No await before the clipboard write: keep it inside the user gesture.
    copySections(packet, sections)
      .then(() => {
        setCopied(key);
        onCopied?.(key);
        setTimeout(() => setCopied(prev => (prev === key ? null : prev)), 2000);
      })
      .catch(err => {
        setCopyError(err instanceof Error ? err.message : 'Could not copy to the clipboard');
      });
  }

  async function handleDownload() {
    if (!packet || downloading) return;
    setDownloading(true);
    setCopyError(null);
    try {
      await downloadXlsx(packet);
    } catch (err) {
      setCopyError(err instanceof Error ? err.message : 'Could not build the spreadsheet');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Export quote details"
        className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 md:rounded-2xl md:max-w-md md:w-full md:pb-6 max-h-[85vh] flex flex-col"
      >
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-1">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-midnight">Export quote details</h2>
            <p className="text-[13px] text-slate mt-0.5 truncate">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <p className="text-[12px] text-slate leading-snug mb-4">
          Copies paste as cells into a spreadsheet and as a table into documents and email.
        </p>

        {loading && !packet ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : error && !packet ? (
          <div className="p-3 bg-rose/10 border border-rose/30 rounded-xl">
            <p className="text-[13px] text-rose font-medium">{error}</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5 overflow-y-auto -mx-1 px-1">
            {OPTIONS.map(opt => {
              const reason = unavailableReason(opt.key);
              const disabled = !packet || reason !== null;
              const isCopied = copied === opt.key;
              const Icon = opt.icon;
              return (
                <li key={opt.key}>
                  <button
                    type="button"
                    onClick={() => handleCopy(opt.key)}
                    disabled={disabled}
                    className={`w-full min-h-[52px] px-3 py-2.5 rounded-xl border text-left flex items-center gap-3 transition-colors
                      ${disabled
                        ? 'border-stone bg-mist/50 opacity-60 cursor-not-allowed'
                        : isCopied
                          ? 'border-emerald-300 bg-emerald-50'
                          : 'border-stone bg-white hover:border-plum/40 active:bg-mist'}`}
                  >
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0
                      ${disabled ? 'bg-stone/60 text-slate' : isCopied ? 'bg-emerald-100 text-emerald-700' : 'bg-plum/10 text-plum'}`}>
                      {isCopied ? <Check size={16} /> : <Icon size={16} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={`block text-[14px] font-semibold ${disabled ? 'text-slate' : 'text-midnight'}`}>
                        {isCopied ? 'Copied' : opt.label}
                      </span>
                      <span className="block text-[12px] text-slate leading-snug">
                        {reason ?? opt.hint}
                      </span>
                    </span>
                    {!disabled && !isCopied && <Copy size={14} className="text-slate shrink-0" />}
                  </button>
                </li>
              );
            })}
            <li className="pt-1">
              <button
                type="button"
                onClick={handleDownload}
                disabled={!packet || downloading}
                className="w-full min-h-[52px] px-3 py-2.5 rounded-xl bg-plum text-white text-left flex items-center gap-3
                           hover:bg-rose active:opacity-80 transition-colors disabled:opacity-50"
              >
                <span className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                  {downloading ? <LoadingSpinner size="sm" /> : <FileSpreadsheet size={16} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14px] font-semibold">
                    {downloading ? 'Building spreadsheet…' : 'Download spreadsheet'}
                  </span>
                  <span className="block text-[12px] text-white/80 leading-snug">
                    One .xlsx file, one tab per section
                  </span>
                </span>
              </button>
            </li>
          </ul>
        )}

        {copyError && (
          <p className="text-[13px] text-rose font-medium mt-3">{copyError}</p>
        )}
      </div>
    </div>
  );
}

/**
 * Small inline copy button for a card header or row: copies one section of
 * the selected version. Renders nothing until the packet is loaded or when
 * the section is missing from the version.
 */
export function CopySectionButton({
  packet,
  section,
  onCopied,
  className = '',
}: {
  packet: QuoteVersionExportPacket | null;
  section: Exclude<ExportCopyKey, 'all'>;
  onCopied?: (key: ExportCopyKey) => void;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  if (!packet || !packet.sections[section]) return null;

  function handleClick() {
    if (!packet) return;
    const sections = sectionsForKey(packet, section);
    if (!sections) return;
    copySections(packet, sections)
      .then(() => {
        setCopied(true);
        onCopied?.(section);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => console.error('[copySection]', err));
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-11 h-11 -my-2 -mr-2 flex items-center justify-center rounded-lg transition-colors shrink-0
        ${copied ? 'text-emerald-600' : 'text-slate hover:text-plum hover:bg-mist'} ${className}`}
      aria-label={copied ? 'Copied' : `Copy ${EXPORT_COPY_LABELS[section].toLowerCase()}`}
      title={copied ? 'Copied' : `Copy ${EXPORT_COPY_LABELS[section].toLowerCase()}`}
    >
      {copied ? <Check size={15} /> : <Copy size={15} />}
    </button>
  );
}
